import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import './EloChart.css';

/* ─────────────────────────────────────────────────────────────
   ELO CHART — line graph of a player's rating history.

   Fetches from rating_history ordered by match_number.
   X-axis toggles between match number (default) and date played.
   Shows an empty state if fewer than 2 data points exist.
   ───────────────────────────────────────────────────────────── */

/* Formats a Unix timestamp (ms) to a short label e.g. "Jan 5" */
function shortDate(ts) {
  return new Date(ts).toLocaleDateString('en-US', {
    month: 'short',
    day:   'numeric',
  });
}

/* Custom tooltip */
function ChartTooltip({ active, payload, xMode }) {
  if (!active || !payload || !payload.length) return null;
  const { match_number, elo_rating, played_at } = payload[0].payload;
  return (
    <div className="elo-chart-tooltip">
      <span className="elo-chart-tooltip-rating">{elo_rating}</span>
      <span className="elo-chart-tooltip-label">
        {xMode === 'date' ? shortDate(payload[0].payload.ts) : `Match #${match_number}`}
      </span>
    </div>
  );
}

function EloChart({ userId, refreshKey, createdAt, showStatsLink = false, startDate = null, isDoubles = false }) {
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [xMode,     setXMode]     = useState('match'); // 'match' | 'date'
  const [tickColor, setTickColor] = useState(() =>
    getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim()
  );

  useEffect(() => {
    if (userId) fetchHistory();
  }, [userId, refreshKey, startDate, isDoubles]);

  // Watch for theme changes and re-read the CSS variable
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTickColor(
        getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim()
      );
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    });
    return () => observer.disconnect();
  }, []);

  async function fetchHistory() {
    setLoading(true);
    let q = supabase
      .from('rating_history')
      .select('elo_rating, match_number, played_at')
      .eq('user_id', userId)
      .eq('is_doubles', isDoubles)
      .order('match_number', { ascending: true });
    if (startDate) q = q.gte('played_at', startDate.toISOString());
    const { data: rows } = await q;
    setData((rows || []).map(r => ({ ...r, ts: new Date(r.played_at).getTime() })));
    setLoading(false);
  }

  // ── Loading ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="elo-chart-loading">
        <span className="elo-chart-star">★</span>
        <span className="elo-chart-star">★</span>
        <span className="elo-chart-star">★</span>
      </div>
    );
  }

  // ── Empty state — not enough matches yet ────────────────────
  if (data.length < 2) {
    return (
      <div className="elo-chart-empty">
        <p className="elo-chart-empty-title">No graph yet</p>
        <p className="elo-chart-empty-sub">
          Play more matches to see your rating history here.
        </p>
      </div>
    );
  }

  // ── Date mode setup ───────────────────────────────────────────
  // Collapse multiple matches on the same calendar day into one point
  // (the last ELO of that day). This prevents the noisy zigzag when
  // a player logs several matches in a single session.
  // Sort by ts here because data is ordered by match_number (ELO application
  // order), but for the date axis we need chronological (report-time) order.
  const dateSorted = [...data].sort((a, b) => a.ts - b.ts);
  const dailyMap = new Map();
  dateSorted.forEach(r => {
    const day = new Date(r.ts).toISOString().slice(0, 10); // "2026-03-15"
    dailyMap.set(day, r); // ascending sort means last entry = end-of-day ELO
  });
  const dailyData = Array.from(dailyMap.values());

  // Build a dataset spanning the full period width.
  // For filtered views (startDate set), left edge = startDate and the
  // starting ELO is whatever the player had entering the period.
  // For lifetime view, left edge = account creation at ELO 1000.
  const domainStart = startDate
    ? startDate.getTime()
    : createdAt ? new Date(createdAt).getTime() : dailyData[0].ts;
  const startElo = startDate ? dailyData[0].elo_rating : 1000;
  const domainEnd   = Date.now();
  const dateData    = [
    { ts: domainStart, elo_rating: startElo,                             match_number: null },
    ...dailyData,
    { ts: domainEnd,   elo_rating: dailyData[dailyData.length - 1].elo_rating, match_number: null },
  ];
  const dateTicks = [domainStart, domainEnd];
  function dateTick(ts) {
    return ts === domainEnd ? 'Today' : shortDate(ts);
  }

  // ── Derive Y-axis domain with a bit of padding ───────────────
  const activeData = xMode === 'date' ? dateData : data;
  const ratings   = activeData.map(d => d.elo_rating);
  const minRating = Math.min(...ratings);
  const maxRating = Math.max(...ratings);
  const padding   = Math.max(50, Math.round((maxRating - minRating) * 0.2));
  const yMin      = Math.max(0, minRating - padding);
  const yMax      = maxRating + padding;

  return (
    <div className="elo-chart-wrap">

      {/* X-axis mode toggle + optional stats link */}
      <div className="elo-chart-toggle-row">
        <div className="elo-chart-toggle-btns">
          <button
            className={`elo-chart-toggle-btn ${xMode === 'match' ? 'active' : ''}`}
            onClick={() => setXMode('match')}
          >
            Match #
          </button>
          <button
            className={`elo-chart-toggle-btn ${xMode === 'date' ? 'active' : ''}`}
            onClick={() => setXMode('date')}
          >
            Date
          </button>
        </div>
        {showStatsLink && (
          <Link to="/statistics" className="elo-chart-stats-link">
            View Statistics →
          </Link>
        )}
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <LineChart key={xMode} data={xMode === 'date' ? dateData : data} margin={{ top: 8, right: 8, bottom: 20, left: 0 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border)"
            vertical={false}
          />
          {xMode === 'match' ? (
            <XAxis
              dataKey="match_number"
              tick={{ fill: tickColor, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              label={{ value: isDoubles ? 'Doubles #' : 'Match #', position: 'insideBottomRight', offset: -4, fill: tickColor, fontSize: 11 }}
            />
          ) : (
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={[domainStart, domainEnd]}
              ticks={dateTicks}
              tickFormatter={dateTick}
              tick={{ fill: tickColor, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
          )}
          <YAxis
            domain={[yMin, yMax]}
            tick={{ fill: tickColor, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={42}
          />
          <Tooltip
            content={<ChartTooltip xMode={xMode} />}
            cursor={{ stroke: 'var(--border)', strokeWidth: 1 }}
          />
          <Line
            type="monotone"
            dataKey="elo_rating"
            stroke="var(--accent)"
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5, fill: 'var(--accent)', strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>

    </div>
  );
}

export default EloChart;
